import { isEqual } from 'lodash'
import { localStorage } from '../../../database'
import { ReservationFormFieldVisibility } from '../../../containers/Form/CreateReservation/withReduxForm'

export type ReservationFormSettingsApi = Record<string, boolean>

const FIELD_API_KEY_MAP: Partial<
  Record<keyof ReservationFormFieldVisibility, string>
> = {
  setCustomColor: 'customColor',
  advancePayment: 'deposit',
  advancePaymentDate: 'prePayment',
  advancePaymentType: 'payDeposit',
  deposit: 'onSitePaymentTypeDeposit',
}

const FIELD_VISIBILITY_KEYS = Object.keys(
  new ReservationFormFieldVisibility(),
) as (keyof ReservationFormFieldVisibility)[]

export function mapFieldVisibilityToApi(
  fieldVisibility: ReservationFormFieldVisibility,
): ReservationFormSettingsApi {
  return FIELD_VISIBILITY_KEYS.reduce(
    (settings, key) => {
      const apiKey = FIELD_API_KEY_MAP[key] || key
      settings[apiKey] = Boolean(fieldVisibility[key])
      return settings
    },
    {} as ReservationFormSettingsApi,
  )
}

export function hasApiReservationFormSettings(
  apiSettings?: ReservationFormSettingsApi | string | null,
): boolean {
  const normalized = normalizeApiSettings(apiSettings)

  return Object.keys(normalized).length > 0
}

export function mapApiToFieldVisibility(
  apiSettings?: ReservationFormSettingsApi | string | null,
): ReservationFormFieldVisibility {
  const normalizedApi = normalizeApiSettings(apiSettings)
  const fieldVisibility = new ReservationFormFieldVisibility()

  FIELD_VISIBILITY_KEYS.forEach(key => {
    const apiKey = FIELD_API_KEY_MAP[key] || key
    if (apiKey in normalizedApi) {
      fieldVisibility[key] = Boolean(normalizedApi[apiKey])
    }
  })

  return fieldVisibility
}

export function readLocalFieldVisibility(): ReservationFormFieldVisibility | null {
  const { UserDriver: Driver, Names } = localStorage
  const { ReservationForm } = Names
  const item = Driver.getItem(ReservationForm.FieldVisibility, '')

  if (!item) {
    return null
  }

  try {
    const parsed = JSON.parse(item)
    const fieldVisibility = new ReservationFormFieldVisibility()

    Object.keys(fieldVisibility).forEach(key => {
      if (key in parsed) {
        fieldVisibility[key] = parsed[key]
      }
    })

    return fieldVisibility
  } catch (e) {
    return null
  }
}

function normalizeApiSettings(
  apiSettings?: ReservationFormSettingsApi | string | null,
): ReservationFormSettingsApi {
  if (!apiSettings) {
    return {}
  }

  if (typeof apiSettings === 'string') {
    try {
      const parsed = JSON.parse(apiSettings)
      return typeof parsed === 'object' && parsed !== null ? parsed : {}
    } catch (e) {
      return {}
    }
  }

  return apiSettings
}

export function isLocalFieldVisibilityDifferentFromApi(
  local: ReservationFormFieldVisibility,
  apiSettings?: ReservationFormSettingsApi | string | null,
): boolean {
  const localApiFormat = mapFieldVisibilityToApi(local)
  const normalizedApi = normalizeApiSettings(apiSettings)

  return !FIELD_VISIBILITY_KEYS.every(key => {
    const apiKey = FIELD_API_KEY_MAP[key] || key
    return isEqual(localApiFormat[apiKey], normalizedApi[apiKey])
  })
}
